/**
 * ConnectionsEmptyState — the desktop first-run fork shown when the user has
 * zero saved connections: connect your own database (primary), import from a
 * signed-in Azure account (live discovery — always offered), and open the
 * bundled sample database (zero-credential path to a results grid). When
 * another tool's connections are detected on this machine (Azure Data Studio,
 * DBeaver, SSMS, …), a dismissible `ImportDiscoveryCard` leads the section —
 * one consolidated offer ("Found 14 connections in …") instead of per-source
 * cards competing inside the grid.
 */

import { useState } from 'react';
import { Plug, Plus, Music } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { openSampleDatabase } from '../../../sample-database';
import { ImportDiscoveryCard, openAzureImportPicker } from '../../../connections/connection-import';
import { AzureIcon } from '../../../connections/azure-picker/AzureIcon';
import {
  openDockerDiscoveryDialog,
  useDockerAvailability,
} from '../../../connections/docker-discovery';

type ConnectionsEmptyStateProps = {
  onAddConnection: () => void;
};

export function ConnectionsEmptyState({ onAddConnection }: ConnectionsEmptyStateProps) {
  const [openingSample, setOpeningSample] = useState(false);
  const { available: dockerAvailable } = useDockerAvailability();

  const handleOpenSample = async () => {
    setOpeningSample(true);
    try {
      await openSampleDatabase('empty_state');
    } finally {
      setOpeningSample(false);
    }
  };

  return (
    <div className="space-y-3">
    <ImportDiscoveryCard />

    <div
      className="grid grid-cols-1 @xl:grid-cols-2 @3xl:grid-cols-3 gap-3"
      data-testid="connections-empty-state"
    >
      <div className="flex flex-col items-center justify-center rounded-md border bg-card px-4 py-6 text-center">
        <Plug className="h-8 w-8 mb-3 opacity-40 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm font-medium">Connect your database</p>
        <p className="text-xs mt-1 text-muted-foreground max-w-[240px] text-pretty">
          SQL Server, PostgreSQL, MySQL, Oracle, SQLite
        </p>
        <Button
          size="sm"
          className="mt-4 active:scale-[0.96] transition-[color,box-shadow,scale]"
          onClick={onAddConnection}
          data-testid="add-connection-empty-state-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Connection
        </Button>
      </div>

      <div className="flex flex-col items-center justify-center rounded-md border bg-card px-4 py-6 text-center">
        <AzureIcon className="h-8 w-8 mb-3" />
        <p className="text-sm font-medium">Import from Azure</p>
        <p className="text-xs mt-1 text-muted-foreground max-w-[240px] text-pretty">
          Pick resources from your signed-in Azure account
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-4 active:scale-[0.96] transition-[color,box-shadow,scale]"
          onClick={() => openAzureImportPicker('empty_state')}
          data-testid="azure-import-empty-state-button"
        >
          Browse Azure
        </Button>
      </div>

      <div className="flex flex-col items-center justify-center rounded-md border bg-card px-4 py-6 text-center">
        <Music className="h-8 w-8 mb-3 opacity-40 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm font-medium">Explore a sample database</p>
        <p className="text-xs mt-1 text-muted-foreground max-w-[240px] text-pretty">
          Chinook music store · 11 tables · no setup, runs locally
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-4 active:scale-[0.96] transition-[color,box-shadow,scale]"
          onClick={handleOpenSample}
          disabled={openingSample}
          data-testid="open-sample-database-button"
        >
          {openingSample ? (
            <>
              <RandomDotmSpinner size={16} />
              <span className="ml-2">Opening…</span>
            </>
          ) : (
            'Open sample'
          )}
        </Button>
      </div>
    </div>

      {dockerAvailable && (
        <p className="text-center text-xs text-muted-foreground" data-testid="docker-discovery-empty-state">
          🐳 Running databases in Docker?{' '}
          <button
            type="button"
            className="font-medium text-primary underline-offset-2 hover:underline px-1 -mx-1 py-2 -my-2"
            onClick={() => void openDockerDiscoveryDialog('empty_state')}
            data-testid="docker-discovery-empty-state-link"
          >
            Detect local databases
          </button>
        </p>
      )}
    </div>
  );
}
